/* 
  Developed by: Jordyn Kao
  Contact: jordyninternational@gmail.com
  This component is part of the application and contains internal credits.
  This code is protected under the terms of the contract agreement.
  Unauthorized use or distribution of this code is prohibited.
*/
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Investment } from '@/types/investment';
import Modal from './ui/Modal';

interface InvestmentCardProps {
  investment: Investment;
}

const InvestmentCard: React.FC<InvestmentCardProps> = ({ investment }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleInvestClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleConfirmRedirect = () => {
    window.open('https://app.alta-x.com', '_blank', 'noopener,noreferrer');
    setIsModalOpen(false);
  };

  return (
    <>
      <div className="bg-gradient-to-b from-[hsl(var(--navy))] to-black text-white rounded-2xl shadow-lg overflow-hidden flex flex-col h-full transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 border border-gray-900 group">
      <div className="relative overflow-hidden h-48 bg-gray-100">
        <img 
          src={investment.imageUrl} 
          alt={investment.name} 
          className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
        <Link to={`/investments/${investment.id}`} className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="text-primary font-semibold">
            Year 1 Guaranteed payback of 25% of Capital & 18.15% Return<br />
            Year 3 Guaranteed payback of 25% of Capital & 36.30% Return<br />
            Year 5 Growth linked to S&amp;P 500
          </span>
          <span className="text-white text-lg font-semibold border-2 border-white rounded-full px-6 py-2">View Details</span>
        </Link>
      </div>
      
      <div className="p-5 sm:p-6 flex-grow flex flex-col">
        <div className="flex justify-between items-start mb-3">
          <h3 className="text-lg sm:text-xl font-bold text-white line-clamp-2">{investment.name}</h3>
          <span className="ml-2 flex-shrink-0 px-3 py-1 rounded-full text-xs font-bold bg-[hsl(var(--bronze-gold-light))] text-[hsl(var(--navy-dark))] whitespace-nowrap">
            {investment.type === 'JSE LISTED INDEX LINKED NOTE' ? 'Guaranteed Capital & Return' : investment.status}
          </span>
        </div>
        
        {investment.type === 'JSE LISTED INDEX LINKED NOTE' ? (
          <p className="text-gray-300 text-sm mb-4 flex-grow whitespace-pre-line">
            100% Capital Protected
            {"\n"}Year 1 Guaranteed payback of 25% of Capital & 18.15% Return
            {"\n"}Year 3 Guaranteed payback of 25% of Capital & 36.30% Return
          </p>
        ) : (
          <p className="text-gray-300 text-sm mb-4 flex-grow whitespace-pre-line">{investment.subtitle}</p>
        )}
        
        <div className="divide-y divide-white/10 text-sm mt-auto border-t border-gray-100 pt-4">
          <div className="flex justify-between py-3">
            <span className="text-gray-400">Type</span>
            <span className="font-medium text-white">
              {investment.type === 'JSE LISTED INDEX LINKED NOTE'
                ? 'ABSA issued JSE Listed Structured Note'
                : investment.id === 'metro-industrial-fund'
                  ? investment.type
                  : investment.type !== investment.category
                    ? `${investment.type} - ${investment.category}`
                    : investment.type}
            </span>
          </div>
          
          {investment.id === 'linked-endowment-guaranteed-investment-series' ? (
            <div className="py-3">
              <div className="mb-3">
                <span className="text-gray-400">Expected Return</span>
              </div>
              <div className="w-full">
                <table className="w-full text-sm border-collapse">
                  <tbody>
                    <tr className="border-b border-white/10">
                      <td className="py-2 font-medium text-white w-16">Year 3</td>
                      <td className="py-2 text-white">45% Capital + 33.54% Return</td>
                    </tr>
                    <tr>
                      <td className="py-2 font-medium text-white w-16">Year 5</td>
                      <td className="py-2 text-white">55% Capital + S&P 500 Growth</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="flex justify-between py-3">
              <span className="text-gray-400">Expected Return</span>
              <span className="font-bold text-[hsl(var(--bronze-gold-light))]">
                {investment.type === 'JSE LISTED INDEX LINKED NOTE' 
                  ? 'Guaranteed and linked to S&P 500' 
                  : investment.return}
              </span>
            </div>
          )}
          
          <div className="flex justify-between py-3">
            <span className="text-gray-400">Min. Investment</span>
            <span className="font-medium text-white">R{investment.minInvestment.toLocaleString()}</span>
          </div>
        </div>

        <div className="mt-5 pt-5 border-t border-white/10">
          <div className="flex flex-col sm:flex-row gap-3">
            <a 
              href="https://app.alta-x.com" 
              onClick={handleInvestClick}
              className="flex-1 text-center bg-[hsl(var(--bronze-gold))] text-black py-2.5 px-4 rounded-lg font-bold hover:bg-[hsl(var(--bronze-gold-light))] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[hsl(var(--bronze-gold))]"
            >
              Invest Now
            </a>
            <Link to={`/investments/${investment.id}`} className="flex-1 text-center bg-white text-black py-2.5 px-4 rounded-lg font-bold hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white">
              Details
            </Link>
            <button className="flex items-center justify-center p-2.5 rounded-lg border border-white/20 hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <span className="sr-only">Add to favorites</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Confirm Redirection">
        <div>
          <p className="text-gray-600 mb-6">
            You will be redirected to our secure investment portal to complete your investment.
          </p>
          <div className="flex justify-end gap-4">
            <button 
              onClick={() => setIsModalOpen(false)}
              className="px-6 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={handleConfirmRedirect}
              className="px-6 py-2 rounded-lg bg-[hsl(var(--navy))] text-white font-bold hover:bg-[hsl(var(--navy-dark))] transition-colors"
            >
              Yes, Continue
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default InvestmentCard;
